import { z } from 'zod';
import { AppPlatform } from '@trinity/chat';
import { AppEnvironment } from '@/types/environment';

export const BoolLikeSchema = z
  .union([z.string(), z.boolean()])
  .transform((value) => (typeof value === 'boolean' ? value : /^(t(?:rue)*)|(y(?:es)*)$/i.test(value)));
export const NumberLikeSchema = z.union([z.string(), z.number()]).pipe(z.coerce.number());

export const AppConfigSchema = z
  .object({
    APP_ENVIRONMENT: AppEnvironment,
    APP_PLATFORM: AppPlatform,

    VEEVA_AUTH_PROVIDER: z.enum(['ping', 'entra']).prefault('entra'),

    BRAND_INSIGHTS_API_URL: z.url(),
    BRAND_INSIGHTS_WS_PATH: z.string(),
    BRAND_INSIGHTS_WS_URL: z.url(),

    FRONT_BASE_URL: z.url(),

    SENTRY_ENABLED: BoolLikeSchema,
    SENTRY_DSN: z.url().optional(),
    SENTRY_RELEASE: z.string().optional(),

    // Browser telemetry (OpenTelemetry): traces and errors, relayed through the API. Off unless enabled per environment.
    OTEL_ENABLED: BoolLikeSchema.prefault(false),
    // Fraction of page loads and requests traced. Runtime env substitution leaves unset values empty, which means "default".
    OTEL_TRACES_SAMPLE_RATE: z.preprocess(
      (value) => (value === '' ? undefined : value),
      NumberLikeSchema.pipe(z.number().min(0).max(1)).prefault(0.5)
    ),

    USERNAME_PASSWORD_LOGIN_ENABLED: BoolLikeSchema,

    DEMO_ENV: BoolLikeSchema.prefault(false),

    OSANO_ENABLED: BoolLikeSchema.prefault(false),
    OSANO_SCRIPT_SRC: z.url().optional(),

    // Trinity Edge SSO
    TRINITY_EDGE_BASE_URL: z.url().optional(),
    TRINITY_EDGE_PLATFORM_ID: z.string().optional(),
  })
  .readonly()
  .refine((data) => !data.SENTRY_ENABLED || (data.SENTRY_ENABLED && data.SENTRY_DSN), {
    path: ['SENTRY_DSN'],
    error: 'SENTRY_DSN is required when SENTRY_ENABLED is true',
  })
  .refine((data) => !data.OSANO_ENABLED || !!data.OSANO_SCRIPT_SRC, {
    path: ['OSANO_SCRIPT_SRC'],
    error: 'OSANO_SCRIPT_SRC is required when OSANO_ENABLED is true',
  });

export type AppConfig = z.infer<typeof AppConfigSchema>;
